'use client';

import { updateCouponOutcome } from '@svenskaspelab/poolbetting/core/action/coupon';
import { getOutcomeCount } from '@svenskaspelab/poolbetting/core/lib/client';
import { useCouponId, useCouponOutcomes } from '@svenskaspelab/poolbetting/core/nextjs/coupon';
import { useEngine } from '@svenskaspelab/poolbetting/core/nextjs/engine';
import type { Outcome } from '@svenskaspelab/poolbetting/schema/coupon';
import type { Draw } from '@svenskaspelab/poolbetting/schema/draw';
import type { EventNumber, EventType, SportType } from '@svenskaspelab/poolbetting/schema/sport';
import { CouponEventOutcomes, CouponOutcome } from '@svenskaspelab/poolbetting/ui/Coupon';
import type { ReactNode } from 'react';

type SportinfoModalBetButtonsProps = {
  event: Draw['events'][EventNumber];
  sportType: SportType;
  eventType: EventType;
};

/**
 * Component that renders the bet buttons in the sportinfo modal.
 *
 * @param root0 props
 * @param root0.event Event object from draw response
 * @param root0.sportType Type of sport (e.g. football, basketball)
 * @param root0.eventType Event type to render outcomes for
 * @returns Bet buttons to check outcomes for coupon in modal
 */
export function SportinfoModalBetButtons({ event, sportType, eventType }: SportinfoModalBetButtonsProps): ReactNode {
  const [couponId, couponIndex] = useCouponId();
  const definition = useEngine();
  const { eventNumber } = event;

  const outcomeCount = getOutcomeCount(sportType, eventType, definition.model);

  const outcomes = useCouponOutcomes({ couponId, couponIndex, eventNumber, groupIndex: 0 });

  const toggleOutcome = (outcomeIndex: Outcome): void => {
    void (async (): Promise<void> => {
      const updatedOutcomes = outcomes.slice();
      const index = updatedOutcomes.indexOf(outcomeIndex);

      if (index === -1) {
        updatedOutcomes.push(outcomeIndex);
      } else {
        updatedOutcomes.splice(index, 1);
      }

      await updateCouponOutcome(updatedOutcomes, { couponId, couponIndex, boardIndex: 0, eventNumber, groupIndex: 0 });
    })();
  };

  return (
    <div className="mt-2 mb-2 flex w-full items-center justify-center">
      <CouponEventOutcomes
        selectedOutcomes={outcomes}
        outcomeCount={outcomeCount}
        className="w-full [&>div]:w-full"
        renderItem={(props) => (
          <CouponOutcome
            {...props}
            key={props.index}
            eventNumber={eventNumber}
            onButtonChange={(index) => {
              toggleOutcome(index as Outcome);
            }}
          />
        )}
      />
    </div>
  );
}
